import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { withRights } from '../../../../../providers/RightsConsumer';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import DocumentsHistory from './DocumentsHistory';
import { Grid, TableBody, TableHead, TableRow, Tooltip, Link, Checkbox } from '@mui/material';
import { Clear, Done, IndeterminateCheckBoxOutlined, ErrorOutlined, CloudDownloadOutlined, History } from '@mui/icons-material';
import { Autocomplete, Button, IconButton, Typography, Dialog } from '@totalsoft/rocket-ui';

import {
  BackdropCustom,
  BulkActionsContainer,
  BulkActionsTriangle,
  DenseBodyTableCell,
  DenseHeaderTableCell,
  DialogButton,
  TableStyled,
  WarningTypography,
  SecondDialogButton
} from './DocumentsStyles';

const DocumentsExport = ({
  t,
  documents,
  downloadFile,
  exportDocumentAndDownload,
  isAnyDocumentChecked,
  onSign,
  onCancelSign,
  signing,
  disableDownload,
  rights,
  printing,
  onPrintAll,
  onDocumentPrintPropertyChange,
  allowIndividualSigningOfDocuments,
  validation,
  modelIsValid,
  onSelectAllDocuments,
  sendDocumentsByEmail,
  sendingEmail,
  closeEmailModal,
  showModal,
  openEmailModal,
  defaultEmail,
  signatureTypesAreEqual
}) => {
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [fileIdHistory, setFileIdHistory] = useState(null);

  const isAnyDocumentSelected = useMemo(() => documents.some(document => document.isSelected), [documents]);
  const selectableDocuments = useMemo(() => documents.filter(document => !document.isSigningInProgress), [documents]);

  const downloadClick = item => () => {
    if (item.isUploaded) downloadFile(item.fileId);
  };

  const downloadExportClick = item => () => {
    if (item?.documentPrintId) exportDocumentAndDownload(item.documentPrintId);
  };

  const documentHistoryClick = item => () => {
    setHistoryModalOpen(true);
    setFileIdHistory(item.fileId);
  };

  const cancelSignClick = item => () => {
    if (item.isUploaded) onCancelSign(item.documentPrintId);
  };

  const handleHistoryModalClose = () => setHistoryModalOpen(false);

  return (
    <>
      <BulkActionsContainer container display='flex' alignItems='center' px={2.5} py={0.75} minHeight={7.5}>
        <Grid item xs='auto' mr={3.5}>
          <Checkbox
            onChange={onSelectAllDocuments}
            checked={selectableDocuments.length > 0 && selectableDocuments.every(x => x.isSelected)}
            indeterminate={!selectableDocuments.every(x => x.isSelected) && isAnyDocumentSelected}
            indeterminateIcon={<IndeterminateCheckBoxOutlined />}
          />
        </Grid>
        <Grid item xs>
          <Grid container spacing={2} display='flex' alignItems='center'>
            <Grid item>{t('OfferEdit.Documents.BulkSelection')}</Grid>
            <Grid item>
              <Grid container spacing={1} display='flex'>
                <Grid item>
                  {signatureTypesAreEqual ? (
                    <Button
                      variant='outlined'
                      color='primary'
                      onClick={onSign}
                      disabled={signing || printing || !modelIsValid || !isAnyDocumentChecked || !rights.documentsExportWrite}
                    >
                      {signing ? t('Signing') : allowIndividualSigningOfDocuments ? t('Sign') : t('SignEligibleDocuments')}
                    </Button>
                  ) : (
                    <Button
                      variant='outlined'
                      color='primary'
                      onClick={onSign}
                      disabled
                      tooltip={t('OfferEdit.Documents.SignatureTypesMessage')}
                    >
                      {allowIndividualSigningOfDocuments ? t('Sign') : t('SignEligibleDocuments')}
                    </Button>
                  )}
                </Grid>
                <Grid item>
                  <Button
                    variant='outlined'
                    color='primary'
                    onClick={onPrintAll}
                    disabled={printing || signing || !rights.printDocuments || !isAnyDocumentSelected}
                  >
                    {printing ? t('Printing') : t('PrintAll')}
                  </Button>
                </Grid>
                <Grid item>
                  {!defaultEmail ? (
                    <Button color='primary' variant='outlined' disabled tooltip={t('OfferEdit.Documents.EmailNotAvailableMessage')}>
                      {t('OfferEdit.Documents.SendEmail')}
                    </Button>
                  ) : (
                    <Button
                      color='primary'
                      variant='outlined'
                      onClick={openEmailModal}
                      disabled={sendingEmail || !isAnyDocumentSelected || !rights.sendDocumentExportsOnEmail}
                    >
                      {sendingEmail ? t('OfferEdit.Documents.SendingEmail') : t('OfferEdit.Documents.SendEmail')}
                    </Button>
                  )}
                  {showModal && (
                    <Dialog
                      open={showModal}
                      onClose={closeEmailModal}
                      BackdropComponent={BackdropCustom}
                      showX={false}
                      disableBackdropClick
                      disableEscapeKeyDown
                      actionsProps={{ sx: theme => ({ px: theme.spacing(3), pb: theme.spacing(2) }) }}
                      content={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Tooltip title={t('OfferEdit.Documents.ContactInfoMessage')}>
                            <ErrorOutlined color='primary' sx={theme => ({ mr: theme.spacing(0.625) })} />
                          </Tooltip>
                          <Typography emphasis='bold'>{t('OfferEdit.Documents.ClientEmail', { defaultEmail })}</Typography>
                        </div>
                      }
                      actions={
                        <>
                          <DialogButton id='sendEmail_acceptBtn' color='primary' autoFocus onClick={sendDocumentsByEmail}>
                            {t('OfferEdit.Documents.AcceptButton')}
                          </DialogButton>
                          <SecondDialogButton id='sendEmail_closeBtn' color='primary' onClick={closeEmailModal}>
                            {t('OfferEdit.Documents.RejectButton')}
                          </SecondDialogButton>
                        </>
                      }
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              {documents.some(x => x.isSelected && !x.needsSigning) && allowIndividualSigningOfDocuments && (
                <Grid container spacing={1} display='flex' alignItems='center'>
                  <Grid item xs={'auto'} py='auto' display='flex' alignItems='center'>
                    <ErrorOutlined color='warning' />
                  </Grid>
                  <Grid item xs>
                    <WarningTypography emphasis='bold'>{t('OfferEdit.Documents.BulkSelectionWarning')}</WarningTypography>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </BulkActionsContainer>
      <BulkActionsTriangle />
      <div style={{ minHeight: '0.1%', overflowX: 'auto' }}>
        <TableStyled>
          <TableHead>
            <TableRow>
              <DenseHeaderTableCell
                sx={theme => ({ width: theme.spacing(10.75), maxWidth: theme.spacing(10.75) })}
                align='center'
                padding='none'
              >
                <Typography variant='inherit' tooltip={t('Selected')} noWrap>
                  {t('Selected')}
                </Typography>
              </DenseHeaderTableCell>
              <DenseHeaderTableCell width='20%' align='left'>
                <Typography variant='inherit' tooltip={t('OfferEdit.Documents.TemplateName')} noWrap>
                  {t('OfferEdit.Documents.TemplateName')}
                </Typography>
              </DenseHeaderTableCell>
              <DenseHeaderTableCell align='center'>
                <Typography variant='inherit' tooltip={t('OfferEdit.Documents.Signable')} noWrap>
                  {t('OfferEdit.Documents.Signable')}
                </Typography>
              </DenseHeaderTableCell>
              <DenseHeaderTableCell width='20%' align='left'>
                <Typography variant='inherit' tooltip={t('OfferEdit.Documents.EntityTitle')} noWrap>
                  {t('OfferEdit.Documents.EntityTitle')}
                </Typography>
              </DenseHeaderTableCell>
              <DenseHeaderTableCell align='left'>
                <Typography variant='inherit' tooltip={t('OfferEdit.Documents.GeneratedBy')} noWrap>
                  {t('OfferEdit.Documents.GeneratedBy')}
                </Typography>
              </DenseHeaderTableCell>
              <DenseHeaderTableCell align='center'>
                <Typography variant='inherit' tooltip={t('OfferEdit.Documents.Status')} noWrap>
                  {t('OfferEdit.Documents.Status')}
                </Typography>
              </DenseHeaderTableCell>
              <DenseHeaderTableCell align='left'>
                <Typography variant='inherit' tooltip={t('OfferEdit.Documents.SignatureType')} noWrap>
                  {t('OfferEdit.Documents.SignatureType')}
                </Typography>
              </DenseHeaderTableCell>
              <DenseHeaderTableCell align='center'>
                <Typography variant='inherit' tooltip={t('OfferEdit.Documents.Actions')} noWrap>
                  {t('OfferEdit.Documents.Actions')}
                </Typography>
              </DenseHeaderTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents?.map((n, index) => {
              return (
                <TableRow key={n.documentPrintId}>
                  <DenseBodyTableCell
                    sx={{
                      '& > *': {
                        display: 'flex',
                        alignItems: 'center'
                      }
                    }}
                  >
                    <Tooltip title={n.isSigningInProgress ? t('OfferEdit.Documents.SigningInProgressTooltip') : ''}>
                      <span>
                        <Checkbox
                          checked={n.isSelected}
                          disabled={n.isSigningInProgress}
                          onChange={onCheckBoxChange(onDocumentPrintPropertyChange(n, 'isSelected'))}
                        />
                      </span>
                    </Tooltip>
                  </DenseBodyTableCell>

                  <DenseBodyTableCell sx={{ fontWeight: 'bold' }}>
                    <Tooltip title={n.isSigningInProgress ? t('OfferEdit.Documents.SigningInProgressTooltip') : ''}>
                      <span>
                        <Link
                          onClick={n.isSigningInProgress ? undefined : downloadExportClick(n)}
                          sx={n.isSigningInProgress ? { pointerEvents: 'none', color: 'text.disabled' } : undefined}
                        >
                          <Typography variant='inherit' tooltip={n.templateName} noWrap>
                            {n.templateName}
                          </Typography>
                        </Link>
                      </span>
                    </Tooltip>
                  </DenseBodyTableCell>
                  <DenseBodyTableCell align='center'>{n.needsSigning ? <Done sx={{ color: 'green' }} /> : <Clear />}</DenseBodyTableCell>
                  <DenseBodyTableCell>
                    <Typography variant='inherit' tooltip={n.entityTitle} noWrap>
                      {n.entityTitle}
                    </Typography>
                  </DenseBodyTableCell>
                  <DenseBodyTableCell>
                    {n.exportedBy ? (
                      <Typography
                        variant='inherit'
                        tooltip={
                          <span>
                            {n.exportedBy}
                            <br />
                            {t('DATE', { date: n.exportedDate })}
                          </span>
                        }
                        noWrap
                      >
                        <span>
                          {n.exportedBy}
                          <br />
                          {t('DATE', { date: n.exportedDate })}
                        </span>
                      </Typography>
                    ) : null}
                  </DenseBodyTableCell>
                  <DenseBodyTableCell align='center'>
                    {n.signingStatus && (
                      <>
                        <Typography
                          variant='inherit'
                          tooltip={
                            <span>
                              {n.signingStatus}
                              <br />
                              {t('DATE', { date: n.signatureDate })}
                            </span>
                          }
                          noWrap
                        >
                          <span>
                            {n.signingStatus}
                            <br />
                            {t('DATE', { date: n.signatureDate })}
                          </span>
                        </Typography>
                        {rights.cancelSignature && (
                          <IconButton
                            type='cancel'
                            variant='text'
                            color='error'
                            size='small'
                            tooltip={t('CancelSign')}
                            onClick={cancelSignClick(n)}
                          />
                        )}
                      </>
                    )}
                  </DenseBodyTableCell>
                  <DenseBodyTableCell>
                    {n.templateForSign && (
                      <Autocomplete
                        fullWidth
                        value={n.signatureTypeId}
                        isSearchable={false}
                        simpleValue={true}
                        onChange={onDocumentPrintPropertyChange(n, 'signatureTypeId')}
                        valueKey='signatureId'
                        labelKey='signatureName'
                        id='partner'
                        options={n.documentTemplateXSignatures}
                        disabled={n.documentTemplateXSignatures.length < 1}
                        error={!isValid(validation[index].signatureTypeId)}
                        helperText={getErrors(validation[index].signatureTypeId)}
                        isClearable={false}
                      />
                    )}
                  </DenseBodyTableCell>
                  <DenseBodyTableCell align='center'>
                    <IconButton
                      variant='text'
                      tooltip={t('Tooltips.Download')}
                      onClick={downloadClick(n)}
                      download
                      disabled={disableDownload || !n.isUploaded || !n.fileId}
                    >
                      <CloudDownloadOutlined />
                    </IconButton>
                    <IconButton
                      variant='text'
                      tooltip={t('Tooltips.DocumentHistory')}
                      onClick={documentHistoryClick(n)}
                      disabled={disableDownload || !n.isUploaded || !n.fileId}
                    >
                      <History />
                    </IconButton>
                  </DenseBodyTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </TableStyled>
      </div>
      {fileIdHistory && historyModalOpen && (
        <DocumentsHistory fileId={fileIdHistory} open={historyModalOpen} onCloseModal={handleHistoryModalClose} />
      )}
    </>
  );
};

DocumentsExport.propTypes = {
  t: PropTypes.func.isRequired,
  documents: PropTypes.array,
  downloadFile: PropTypes.func.isRequired,
  exportDocumentAndDownload: PropTypes.func.isRequired,
  isAnyDocumentChecked: PropTypes.bool,
  onSign: PropTypes.func.isRequired,
  onCancelSign: PropTypes.func.isRequired,
  signing: PropTypes.bool,
  disableDownload: PropTypes.bool.isRequired,
  rights: PropTypes.object.isRequired,
  printing: PropTypes.bool,
  onPrintAll: PropTypes.func.isRequired,
  allowIndividualSigningOfDocuments: PropTypes.bool,
  onDocumentPrintPropertyChange: PropTypes.func,
  validation: PropTypes.object.isRequired,
  modelIsValid: PropTypes.bool.isRequired,
  onSelectAllDocuments: PropTypes.func.isRequired,
  sendDocumentsByEmail: PropTypes.func.isRequired,
  sendingEmail: PropTypes.bool.isRequired,
  showModal: PropTypes.bool.isRequired,
  openEmailModal: PropTypes.func.isRequired,
  closeEmailModal: PropTypes.func.isRequired,
  defaultEmail: PropTypes.string,
  signatureTypesAreEqual: PropTypes.bool.isRequired
};

export default withTranslation()(withRights(DocumentsExport));
